// data-layer/services/auth.ts
import { api, authStorage } from "../libs/baseApi";
import { ApiResponse, LoginRequest, SetPasswordRequest, User } from "../types";
import { AxiosRequestConfig } from "axios";

// A simple login function.
export const login = async (
  credentials: LoginRequest,
  config?: AxiosRequestConfig,
) => {
  try {
    // This is a normal Axios POST request. The interceptor will not
    // attach a token since we haven't stored one yet.
    const response = await api.post("/auth/login", credentials, config);

    // Token storage is handled in the login mutation
    return response.data;
  } catch (error) {
    // Handle errors like wrong credentials.
    throw error;
  }
};

// A logout function.
export const logout = async () => {
  try {
    // Get the current auth data to access the refresh token
    const authDataResult = authStorage!.getAuthData();
    const currentAuthData =
      authDataResult instanceof Promise ? await authDataResult : authDataResult;

    // Send the refresh token in the request body for proper logout
    const requestBody = currentAuthData?.refresh_token
      ? { refresh_token: currentAuthData.refresh_token }
      : {};

    const response = await api.post("/auth/logout", requestBody);
    if (response.data) {
      // We can clear the auth data from storage, and the interceptor will
      // no longer attach a token to subsequent requests.
      authStorage!.clearAuthData();
      return response.data;
    }
  } catch (error) {
    // Even if logout fails on the backend, clear local auth data
    authStorage!.clearAuthData();
    throw error;
  }
};

export const setPassword = async (
  payload: SetPasswordRequest,
  config?: AxiosRequestConfig,
) => {
  try {
    // This is a normal Axios POST request. The interceptor will not
    // attach a token since we haven't stored one yet.
    const response = await api.post(
      "/auth/set-password",
      {
        new_password: payload.new_password,
      },
      {
        ...config,
        headers: {
          Authorization: `Bearer ${payload.access_token}`,
          ...config?.headers,
        },
      },
    );

    // Token storage is handled in the login mutation

    return response.data;
  } catch (error) {
    // Handle errors like wrong credentials.
    throw error;
  }
};

// API function for refreshing tokens
export const refreshTokensApi = async (refreshToken: string) => {
  const response = await api.post<ApiResponse<any>>("/auth/refresh", {
    refresh_token: refreshToken,
  });
  return response.data;
};

export const getProfile = async () => {
  const response = await api.get<ApiResponse<User>>("/users/profile");
  return response.data;
};
